
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileUp, Loader2, File, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface ResumeUploadProps {
  isUploading: boolean;
  setIsUploading: (isUploading: boolean) => void;
  onUpload: (content: string, filename: string) => void;
  uploadComplete: boolean;
  filename: string;
}

const ResumeUpload = ({ isUploading, setIsUploading, onUpload, uploadComplete, filename }: ResumeUploadProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [dragActive, setDragActive] = useState(false);
  const [fileSelected, setFileSelected] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [retryAttempts, setRetryAttempts] = useState(0);
  const [extractionInProgress, setExtractionInProgress] = useState(false);
  const MAX_RETRY_ATTEMPTS = 3; // Set a reasonable maximum number of retries
  const retryAttemptsRef = useRef(0); // Use a ref to track actual retry count

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const extractTextFromResume = async (resumeId: string): Promise<string> => {
    setExtractionInProgress(true);
    try {
      // Use the ref for tracking actual attempts
      const currentAttempt = retryAttemptsRef.current + 1;
      console.log(`Attempting text extraction for resume ID: ${resumeId}, attempt ${currentAttempt}/${MAX_RETRY_ATTEMPTS}`);
      
      const { data: extractedData, error: extractError } = await supabase.functions.invoke('extract-text', {
        body: { resumeId: resumeId }
      });

      if (extractError) {
        console.error('Text extraction error:', extractError);
        throw extractError;
      }

      if (!extractedData?.content) {
        throw new Error('No text content extracted');
      }

      console.log('Text extraction successful, content length:', extractedData.content.length);
      // Reset retries on success
      retryAttemptsRef.current = 0;
      return extractedData.content;
    } catch (error: any) {
      console.error('Text extraction attempt failed:', error);
      
      // Increment the ref counter first
      retryAttemptsRef.current += 1;
      // Update the state for UI display
      setRetryAttempts(retryAttemptsRef.current);
      
      // If we have retries left, wait and try again
      if (retryAttemptsRef.current < MAX_RETRY_ATTEMPTS) {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
        return extractTextFromResume(resumeId);
      }
      
      throw error;
    } finally {
      setExtractionInProgress(false);
    }
  };

  const handleFile = async (file: File) => {
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    
    if (!['pdf', 'docx'].includes(fileExt || '')) {
      toast({
        title: "Invalid File Type",
        description: "Please upload a PDF or DOCX file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB
      toast({
        title: "File Too Large",
        description: "Maximum file size is 5MB",
        variant: "destructive",
      });
      return;
    }

    try {
      setFileSelected(file.name);
      setIsUploading(true);
      setUploadSuccess(false);
      // Reset retry attempts before starting
      setRetryAttempts(0);
      retryAttemptsRef.current = 0;
      
      // Reset state
      const filePath = `${user?.id}/${Date.now()}.${fileExt}`;
      
      // Step 1: Upload file to storage
      const { error: uploadError, data: uploadData } = await supabase.storage
        .from('resumes')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Step 2: Create resume record in database
      const { data: resumeData, error: dbError } = await supabase
        .from('resumes')
        .insert({
          user_id: user?.id,
          content: null,
          title: `Original Resume - ${file.name}`,
          is_original: true,
          original_filename: file.name,
          status: 'pending',
          preview_content: uploadData.path
        })
        .select()
        .single();

      if (dbError) {
        await supabase.storage.from('resumes').remove([filePath]);
        throw dbError;
      }

      // Show processing toast
      toast({
        title: "Processing Resume",
        description: "Your resume is being processed, please wait...",
      });
      
      // Step 3: Extract text from file
      try {
        const extractedText = await extractTextFromResume(resumeData.id);
        
        // Successfully uploaded and extracted
        setUploadSuccess(true);
        onUpload(extractedText || '', file.name);
        
        toast({
          title: "Resume Uploaded",
          description: `Successfully uploaded ${file.name}. You can now continue with the optimization.`,
        });
      } catch (extractionError: any) {
        console.error('Final extraction error:', extractionError);
        
        // Even if extraction fails, try to get the resume content directly from the database
        // This serves as a fallback in case the extraction function failed but still saved content
        const { data: resumeWithContent } = await supabase
          .from('resumes')
          .select('content')
          .eq('id', resumeData.id)
          .single();
          
        if (resumeWithContent?.content) {
          // If we got content from the database despite the extraction error
          setUploadSuccess(true);
          onUpload(resumeWithContent.content, file.name);
          
          toast({
            title: "Resume Uploaded",
            description: `Successfully uploaded ${file.name}. You can now continue with the optimization.`,
          });
        } else {
          // Manual content extraction fallback for simple files
          try {
            // For DOCX files, provide a basic fallback
            if (fileExt === 'docx') {
              const fallbackText = "Your resume content could not be automatically extracted. " +
                "Please consider uploading a simpler format or manually entering your resume details.";
              
              // Update the resume with fallback text
              await supabase
                .from('resumes')
                .update({
                  content: fallbackText,
                  status: 'completed'
                })
                .eq('id', resumeData.id);
              
              setUploadSuccess(true);
              onUpload(fallbackText, file.name);
              
              toast({
                title: "Resume Uploaded with Limited Content",
                description: "We couldn't fully process your resume. You can continue, but consider uploading a PDF file for better results.",
                variant: "default",
              });
            } else {
              // Complete extraction failure
              toast({
                title: "Text Extraction Failed",
                description: "Unable to extract text from your file. Please try a different file or format.",
                variant: "destructive",
              });
              
              setIsUploading(false);
            }
          } catch (fallbackError) {
            console.error("Fallback extraction failed:", fallbackError);
            toast({
              title: "Text Extraction Failed",
              description: "Unable to process your resume. Please try again with a different file.",
              variant: "destructive",
            });
            
            setIsUploading(false);
          }
        }
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      setIsUploading(false);
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload the resume file",
        variant: "destructive",
      });
    }
  };

  const getFileIcon = () => {
    if (isUploading || extractionInProgress) {
      return <Loader2 className="w-12 h-12 text-gray-400 animate-spin" />;
    }
    if (uploadComplete) {
      const fileExt = filename.split('.').pop()?.toLowerCase();
      return fileExt === 'pdf' ? 
        <FileText className="w-12 h-12 text-green-500" /> : 
        <FileText className="w-12 h-12 text-green-500" />;
    }
    return <FileUp className="w-12 h-12 text-gray-400" />;
  };

  // Reset retry attempts if component is unmounted or uploadComplete changes
  useEffect(() => {
    return () => {
      retryAttemptsRef.current = 0;
      setRetryAttempts(0);
    };
  }, [uploadComplete]);

  return (
    <div className="space-y-4">
      <Label htmlFor="resume">Upload Resume</Label>
      <div 
        className={cn(
          "relative border-2 border-dashed rounded-lg transition-all duration-200",
          dragActive ? "border-primary bg-primary/5" : "border-gray-300 bg-gray-50",
          uploadComplete ? "border-green-500 bg-green-50" : "",
          "hover:border-primary hover:bg-primary/5"
        )}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <Label
          htmlFor="resume-upload"
          className="flex flex-col items-center justify-center w-full min-h-[200px] cursor-pointer p-8"
        >
          <div className="flex flex-col items-center justify-center gap-4">
            {getFileIcon()}
            <div className="text-center">
              <p className="text-lg font-medium text-gray-700">
                {isUploading || extractionInProgress ? 
                  (extractionInProgress ? "Extracting text..." : "Uploading...") : 
                 uploadComplete ? `Ready: ${filename}` :
                 "Drop your resume here or click to upload"}
              </p>
              <p className="mt-2 text-sm text-gray-500">
                {uploadComplete 
                  ? "You can optimize this resume for different jobs or upload a new one"
                  : "Accepted formats: PDF, DOCX (Max 5MB)"}
              </p>
              {retryAttempts > 0 && extractionInProgress && (
                <p className="mt-1 text-xs text-amber-600">
                  Retrying text extraction (Attempt {retryAttempts}/{MAX_RETRY_ATTEMPTS})...
                </p>
              )}
            </div>
            {uploadComplete && (
              <Badge variant="outline" className="bg-green-100 text-green-800 mt-2">
                Resume ready
              </Badge>
            )}
          </div>
          <Input
            id="resume-upload"
            type="file"
            accept=".pdf,.docx"
            className="hidden"
            onChange={handleFileUpload}
            disabled={isUploading || extractionInProgress}
          />
        </Label>
      </div>
    </div>
  );
};

export default ResumeUpload;
